import {useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {createMileageRate, getCurrentMileageRate} from "@/services/mileage-rate-services";
import {MileageRate} from "@/models/mileage-rate";
import {Button} from "@/components/ui/button";
import {useToast} from "@/hooks/use-toast";
import {Input} from "@/components/tailwind/input";
import {AlertCircle, Check, Pencil} from "lucide-react";
import {format, parseISO} from "date-fns";
import {de} from "date-fns/locale";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {useAdminTranslation} from "@/i18n/hooks";
import {Text} from "@/components/tailwind/text.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import log from '@/lib/logger'

interface MileageRateSettingProps {
    isMissing?: boolean;
}

export default function MileageRateSetting({ isMissing = false }: MileageRateSettingProps) {
    const {token} = useAuth();
    const {toast} = useToast();
    const {t} = useAdminTranslation();
    const [currentRate, setCurrentRate] = useState<MileageRate | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newAmountPerKm, setNewAmountPerKm] = useState<string>("");
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        loadCurrentRate();
    }, [token]);

    async function loadCurrentRate() {
        setIsLoading(true);
        try {
            const rate = await getCurrentMileageRate(token);
            if (rate) {
                setCurrentRate(rate);
            } else {
                setCurrentRate(null);
            }
        } catch (error) {
            log.error("Error fetching current mileage rate:", error);
            // Removed toast notification for missing mileage rate
        } finally {
            setIsLoading(false);
        }
    }

    function startEditing() {
        setNewAmountPerKm(currentRate ? currentRate.amountPerKm.toString().replace(".", ",") : "");
        setIsEditing(true);
        setValidationError(null);
    }

    function cancelEditing() {
        setIsEditing(false);
        setValidationError(null);
    }

    function validateAmount(value: string): boolean {
        const normalizedValue = value.replace(",", ".");
        const numberValue = parseFloat(normalizedValue);
        if (isNaN(numberValue) || numberValue <= 0) {
            setValidationError(t("adminSettings.mileageRate.errors.invalid-mileage-value"));
            return false;
        }
        setValidationError(null);
        return true;
    }

    async function saveNewRate() {
        if (!validateAmount(newAmountPerKm)) return;

        const amount = parseFloat(newAmountPerKm.replace(",", "."));

        setIsSaving(true);
        try {
            await createMileageRate(token, amount);

            toast({
                title: t("adminSettings.mileageRate.toast.settingSuccessful.title"),
                description: t("adminSettings.mileageRate.toast.settingSuccessful.description"),
            });

            setIsEditing(false);
            loadCurrentRate();
        } catch (error) {
            log.error("Error persisting mileage rate:", error);
            toast({
                title: t("adminSettings.mileageRate.toast.settingError.title"),
                description: t("adminSettings.mileageRate.toast.settingError.description"),
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    }

    function formatCurrency(amount: number) {
        return new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    }

    function formatDate(dateString: string) {
        return format(parseISO(dateString), "dd.MM.yyyy, HH:mm", {locale: de});
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                        {t("adminSettings.mileageRate.title")}
                        {isMissing && (
                            <span 
                                title={t("adminSettings.missingConfiguration.tooltip")}
                                aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                            >
                                <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                            </span>
                        )}
                    </CardTitle>

                    {!isEditing && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={startEditing}
                            className="flex items-center"
                            aria-label={t("adminSettings.mileageRate.button.edit.ariaLabel")}
                            data-testid="add-mileage-rate-button"
                        >
                            <Pencil className="h-4 w-4 mr-1"/>
                            {t(!currentRate || currentRate.amountPerKm === 0
                                    ? "adminSettings.mileageRate.button.edit.text.set"
                                    : "adminSettings.mileageRate.button.edit.text.update")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent>

                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t("adminSettings.mileageRate.loading")}
                    </div>
                ) : isEditing ? (
                    <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                            <Input
                                type="text"
                                value={newAmountPerKm}
                                onChange={(e) => setNewAmountPerKm(e.target.value)}
                                placeholder="0,30"
                                className="w-32"
                                invalid={!!validationError}
                                autoFocus
                                aria-label={t("adminSettings.mileageRate.rateInput.ariaLabel")}
                                data-testid="mileage-rate-amount"
                            />
                            <Text>{t("adminSettings.mileageRate.rate-suffix.input")}</Text>
                        </div>

                        {validationError && (
                            <ValidationAlert message={validationError}/>
                        )}

                        <div className="flex space-x-2">
                            <Button
                                size="sm"
                                onClick={saveNewRate}
                                disabled={isSaving}
                                className="flex items-center"
                                aria-label={isSaving
                                    ? t("adminSettings.mileageRate.button.save.ariaLabel.saving")
                                    : t("adminSettings.mileageRate.button.save.ariaLabel.save")}
                                data-testid="submit-mileage-rate"
                            >
                                <Check className="h-4 w-4 mr-1"/>
                                {t(isSaving
                                    ? "adminSettings.mileageRate.button.save.text.saving"
                                    : "adminSettings.mileageRate.button.save.text.save")}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                disabled={isSaving}
                                aria-label={isSaving
                                    ? t("adminSettings.mileageRate.button.cancel.ariaLabel.saving")
                                    : t("adminSettings.mileageRate.button.cancel.ariaLabel.active")}
                            >
                                {t("adminSettings.mileageRate.button.cancel.text")}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-baseline mb-1">
                          <span className="text-2xl font-medium mr-2" data-testid="mileage-rate-display">
                            {currentRate ? formatCurrency(currentRate.amountPerKm) : "0,00 €"}
                          </span>

                            <Text className="text-sm">
                                {t("adminSettings.mileageRate.rate-suffix.display")}
                            </Text>
                        </div>

                        {currentRate && currentRate.amountPerKm > 0 && (
                            <Text>
                                {t("adminSettings.mileageRate.last-set-at", {date: formatDate(currentRate.createdAt)})}
                            </Text>
                        )}

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <p>
                                {t("adminSettings.mileageRate.description")}
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
